import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';

import { discountCodeChangeModalState, setIsDiscountCodeForUpdate, refreshTableDiscountCode, updateDiscountCode, createDiscountCode } from '../../../redux/actions/discountCode.actions';
import useAppDispatch from "../../../redux/hooks/useAppDispatch";
import { selectDiscountCodeForUpdate, selectDiscountCodeRefreshTable, selectIsUpdateDiscountCode } from "../../../redux/selectors/discountCode.selectors";
import { TableProperties } from "../../../shared/components/ui/Table/types";
import { DataDiscountCode } from "../../../redux/@types";
import { validateCreateDiscountCode, validateUpdateDiscountCode } from "../../../helpers";
import { notify } from "../../../shared/utils/notify";

type DiscountCodeSubmitPayload = Omit<
  DataDiscountCode,
  'value_amount' | 'value_percentage'
> & {
  value_amount: number | null;
  value_percentage: number | null;
};

export const useDiscountCode = () => {
  const dispatch = useAppDispatch();

  const isUpdateDiscountCode = useSelector(selectIsUpdateDiscountCode)
  const dataUpdateDiscountCode = useSelector(selectDiscountCodeForUpdate)
  const isRefreshTableDiscountCode = useSelector(selectDiscountCodeRefreshTable)

  const [isLoading, setIsLoading] = useState(false);
  const [tableProps, setTableProps] = useState<TableProperties>();

  const changeModalDiscountCodeState = () => {
    dispatch(setIsDiscountCodeForUpdate(false))
    dispatch(discountCodeChangeModalState())
  }

  const formMethodsCreate = useForm<DataDiscountCode>({
    resolver: yupResolver(validateCreateDiscountCode),
  });

  const formMethodsUpdate = useForm<DataDiscountCode>({
    resolver: yupResolver(validateUpdateDiscountCode),
  });

  useEffect(() => {
    if (isUpdateDiscountCode) {
      formMethodsCreate.reset();
      formMethodsUpdate.reset({ ...dataUpdateDiscountCode });
    }

    return () => {
      formMethodsUpdate.reset();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUpdateDiscountCode, dataUpdateDiscountCode]);

  const updateStatus = async(value: "active" | "inactive", id: any) =>{
    if (isLoading) return;
    setIsLoading(true);
    await updateDiscountCode({
      id,
      status: value,
    })
    .then((res: Partial<DataDiscountCode>) => {
      setIsLoading(false);
      if (res.id) {
        // dispatch(bannersChangeModalState());
        notify.success(
          `Se ha actualizado con éxito el estado del código con id:${res.id}`
        );
        dispatch(refreshTableDiscountCode(true));
      } else {
        notify.error(String(res));
      }
    })
    .catch((err: any) => {
      setIsLoading(false);
      console.log(err);
      notify.error(err.message);
    });
  }

  const onSubmitCreateOrUpdate = async (data: DataDiscountCode) => {
    if (isLoading) return;
    setIsLoading(true);

    const fixedAmount = Number(data.value_amount) || 0;
    const percentage = Number(data.value_percentage) || 0;
    const normalizedData: DiscountCodeSubmitPayload = {
      ...data,
      value_amount: fixedAmount > 0 ? fixedAmount : null,
      value_percentage: fixedAmount > 0 ? null : percentage > 0 ? percentage : null,
    };

    if (isUpdateDiscountCode) {
      await updateDiscountCode({
        id: normalizedData.id,
        status: normalizedData.status,
        name: normalizedData.name,
        code: normalizedData.code,
        quantity: normalizedData.quantity,
        value_percentage: normalizedData.value_percentage,
        value_amount: normalizedData.value_amount,
        uses_by_user: normalizedData.uses_by_user,
        date_start: normalizedData.date_start,
        date_end: normalizedData.date_end,
        order_min_amount: normalizedData.order_min_amount,
      })
        .then((res: Partial<DataDiscountCode>) => {
          setIsLoading(false);
          if (res?.id || normalizedData.id) {
            changeModalDiscountCodeState();
            notify.success(
              `Se ha actualizado con éxito el código de descuento con id:${res?.id ?? normalizedData.id}`
            );
            dispatch(refreshTableDiscountCode(true));
          } else {
            notify.error(typeof res === "string" ? res : "No se pudo actualizar el cupón");
          }
        })
        .catch((err: any) => {
          setIsLoading(false);
          console.log(err);
          notify.error(err.message);
        });
    } else {
      await createDiscountCode(normalizedData)
        .then((res: DataDiscountCode) => {
          setIsLoading(false);
          if (res.id) {
            changeModalDiscountCodeState();
            notify.success(
              `Se ha creado con éxito el código de descuento con id:${res.id}`
            );
            dispatch(refreshTableDiscountCode(true));
          } else {
            notify.error(String(res));
          }
        })
        .catch((err: any) => {
          setIsLoading(false);
          console.log(err);
          notify.error(err.message);
        });
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (isRefreshTableDiscountCode) {
      tableProps?.refresh();
      dispatch(refreshTableDiscountCode(false));
    }
  }, [isRefreshTableDiscountCode]);
  return {
    //state
    isUpdateDiscountCode,
    isLoading,
    tableProps,
    //methods
    formMethodsCreate,
    formMethodsUpdate,
    setTableProps,
    //functions
    onSubmitCreateOrUpdate,
    changeModalDiscountCodeState,
    updateStatus,
  }
}
